import { mutateUpload, type Ack } from './api'
import { getMe } from './me'
import { toast } from './ui'

// Unsent photos and voice notes, kept on the phone (worker review 2026-09-24,
// Grok 4.7 finding 5). Until now a shot or a recording that could not be sent
// lived only in the page's memory with a Retry button. In a dead zone the phone
// puts the page away, and the gate photo or the spoken "code changed to 4491"
// was gone. IndexedDB can hold the bytes where localStorage cannot.
//
// One record per job and kind, stored under the signed-in person AND business,
// so a draft is only ever offered back to the account that made it (the same
// separation the offline queue keeps). A record carries the upload's own
// Idempotency-Key, so sending it again is the same request, and the server
// answers a repeat with the saved result rather than a second copy.
//
// Every call is best effort: with no IndexedDB (a private window, storage
// blocked) nothing is kept and the app behaves exactly as it did before.

export type DraftKind = 'photo' | 'audio'

export interface StoredDraft {
  id: string
  owner: string
  kind: DraftKind
  cleanId: number
  blob: Blob
  key: string
  // photo
  label?: string | null
  width?: number
  height?: number
  // voice note: the recorder's type and the visit version it was recorded on
  mime?: string
  version?: number
  // photo: false while it still waits for its label, so it is never sent
  // without one behind the worker's back
  ready?: boolean
  // sending it again cannot help (nothing heard, refused): left for the
  // worker to see on the card, never sent automatically
  hold?: boolean
  savedAt: number
}

const DB_NAME = 'roundly-media'
const STORE = 'drafts'
// A draft nobody has sent in a fortnight is not coming back.
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

let opening: Promise<IDBDatabase | null> | null = null

function openDb(): Promise<IDBDatabase | null> {
  if (opening) return opening
  opening = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null)
        return
      }
      const req = indexedDB.open(DB_NAME, 1)
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' })
      }
      req.onsuccess = () => {
        // Safari can close the connection while the home-screen app is put
        // away: forget it, and the next call opens a new one.
        req.result.onclose = () => {
          opening = null
        }
        resolve(req.result)
      }
      req.onerror = () => {
        opening = null // try again next time rather than keep nothing for good
        resolve(null)
      }
      req.onblocked = () => {
        opening = null
        resolve(null)
      }
    } catch {
      opening = null
      resolve(null)
    }
  })
  return opening
}

function request<T>(mode: IDBTransactionMode, make: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve) => {
        if (!db) {
          resolve(undefined)
          return
        }
        try {
          const req = make(db.transaction(STORE, mode).objectStore(STORE))
          req.onsuccess = () => resolve(req.result)
          req.onerror = () => resolve(undefined)
        } catch {
          // A connection Safari has closed throws here: open afresh next time.
          opening = null
          resolve(undefined)
        }
      }),
  )
}

// Who the drafts belong to: the signed-in person inside their business.
function owner(): string | null {
  const me = getMe()
  if (!me?.email || me.businessId == null) return null
  return `${me.email.toLowerCase()}|${me.businessId}`
}

function idFor(kind: DraftKind, cleanId: number, who: string): string {
  return `${kind}:${who}:${cleanId}`
}

export async function putDraft(d: Omit<StoredDraft, 'id' | 'owner' | 'savedAt'>): Promise<void> {
  const who = owner()
  if (!who) return
  await request('readwrite', (s) => s.put({ ...d, id: idFor(d.kind, d.cleanId, who), owner: who, savedAt: Date.now() }))
}

export async function getDraft(kind: DraftKind, cleanId: number): Promise<StoredDraft | null> {
  const who = owner()
  if (!who) return null
  const found = (await request<StoredDraft>('readonly', (s) => s.get(idFor(kind, cleanId, who)))) ?? null
  if (!found || !(found.blob instanceof Blob)) return null
  if (Date.now() - found.savedAt > MAX_AGE_MS) {
    await removeDraft(kind, cleanId)
    return null
  }
  return found
}

export async function removeDraft(kind: DraftKind, cleanId: number): Promise<void> {
  const who = owner()
  if (!who) return
  await request('readwrite', (s) => s.delete(idFor(kind, cleanId, who)))
}

// ---- sending them by themselves ---------------------------------------------------
//
// Liam, 2026-09-24: unsent photos and voice notes "should send themselves as
// soon as the signal comes back". Every draft kept for the signed-in account
// is tried when the signal returns, when the phone comes back to the front,
// once a minute while the app is open, and at start-up: whichever job it is
// on, whether or not that job is on screen. Each is sent with its own key, so
// a copy the server already has is answered with the saved one, never a
// second. One pass at a time; a pass stops at the first "no signal".

// One channel for everything that happens to a job's unsent photo or voice
// note, so every card showing that job agrees (review 2026-09-24): a copy
// being sent (no Discard offered mid-send), one that failed and waits again,
// one that reached the round (with the saved row), or one thrown away or
// refused for good. `from` is the card that said it, so it can ignore itself.
export type MediaWhat = 'sending' | 'unsent' | 'sent' | 'cleared'
export interface MediaState {
  kind: DraftKind
  cleanId: number
  what: MediaWhat
  row?: unknown
  error?: string
  from?: object
}
export function announce(state: MediaState): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function' || typeof CustomEvent !== 'function') return
  window.dispatchEvent(new CustomEvent<MediaState>('roundly:media-state', { detail: state }))
}

function audioExt(mime: string | undefined): string {
  const m = mime ?? ''
  if (m.includes('webm')) return 'webm'
  if (m.includes('ogg')) return 'ogg'
  if (m.includes('wav')) return 'wav'
  if (m.includes('mpeg')) return 'mp3'
  return 'm4a'
}

function upload(d: StoredDraft): Promise<Ack<unknown>> {
  if (d.kind === 'photo') {
    return mutateUpload({
      path: `/api/cleans/${d.cleanId}/photos`,
      key: d.key,
      fields: {
        ...(d.label ? { label: d.label } : {}),
        ...(d.width ? { width: String(d.width) } : {}),
        ...(d.height ? { height: String(d.height) } : {}),
      },
      file: d.blob,
      filename: 'photo.jpg',
      fileField: 'image',
      ackField: 'media',
    })
  }
  return mutateUpload({
    path: `/api/cleans/${d.cleanId}/voice`,
    key: d.key,
    fields: { version: String(d.version ?? 1) },
    file: d.blob,
    filename: `voice-note.${audioExt(d.mime)}`,
  })
}

async function store(d: StoredDraft): Promise<void> {
  await request('readwrite', (s) => s.put(d))
}

let passRunning = false

// Photos whose label picker is open on THIS page right now. Only those wait
// for their label. A photo stored as waiting for one, but whose page has
// gone (closed, reloaded, dropped by the phone), has nobody choosing any
// more, and goes without a label (found 2026-09-24 on Liam's phone: the
// photo was kept as "waiting for its label", so it was never sent).
const choosingNow = new Set<number>()
export function markChoosing(cleanId: number, choosing: boolean): void {
  if (choosing) choosingNow.add(cleanId)
  else choosingNow.delete(cleanId)
}

export async function sendKeptDrafts(): Promise<void> {
  if (passRunning) return
  const who = owner()
  if (!who) return
  passRunning = true
  let photos = 0
  let notes = 0
  try {
    const all = ((await request<StoredDraft[]>('readonly', (s) => s.getAll())) ?? []).filter((d) => d.owner === who)
    for (const d of all) {
      if (Date.now() - d.savedAt > MAX_AGE_MS) {
        await request('readwrite', (s) => s.delete(d.id))
        continue
      }
      if (d.hold || (d.kind === 'photo' && d.ready === false && choosingNow.has(d.cleanId))) continue
      // Still there, and still the same one? It may have been thrown away,
      // or replaced by a newer shot, since the list was read.
      const fresh = await request<StoredDraft>('readonly', (s) => s.get(d.id))
      if (!fresh || fresh.key !== d.key) continue
      announce({ kind: d.kind, cleanId: d.cleanId, what: 'sending' })
      let ack = await upload(d)
      // A voice note recorded before an edit elsewhere: the server wrote
      // nothing and handed back the version to use, so it goes again at once.
      if (!ack.ok && ack.status === 409 && d.kind === 'audio') {
        const v = (ack.current as { version?: unknown } | undefined)?.version
        if (typeof v === 'number') {
          d.version = v
          await store(d)
          ack = await upload(d)
        }
      }
      if (ack.ok) {
        // Removed only if it is still this one: a newer shot for the same job,
        // taken while this was on its way, stays.
        const now = await request<StoredDraft>('readonly', (s) => s.get(d.id))
        if (now && now.key === d.key) await request('readwrite', (s) => s.delete(d.id))
        if (d.kind === 'photo') photos++
        else notes++
        announce({ kind: d.kind, cleanId: d.cleanId, what: 'sent', row: ack.row })
        continue
      }
      announce({ kind: d.kind, cleanId: d.cleanId, what: 'unsent', error: ack.status === 0 ? undefined : ack.error })
      // Still no signal, signed out, or the phone is on another account now:
      // nothing more will go on this pass.
      if (ack.status === 0 || ack.signedOut) break
      // A photo whose key the server will not take again: a fresh key, tried
      // on the next pass (what Retry on the card does).
      if (ack.status === 409 && d.kind === 'photo') {
        d.key = crypto.randomUUID()
        await store(d)
        continue
      }
      // The server is having trouble: tried again next pass.
      if (ack.status >= 500 && ack.status !== 502) continue
      // Nothing heard, transcription failed, too large, not theirs: sending
      // again will not help. Kept for the worker to see and decide.
      d.hold = true
      await store(d)
    }
  } finally {
    passRunning = false
  }
  if (photos || notes) {
    const said = [photos ? `${photos} photo${photos === 1 ? '' : 's'}` : '', notes ? `${notes} voice note${notes === 1 ? '' : 's'}` : ''].filter(Boolean).join(' and ')
    toast(`Signal back: ${said} sent`)
    window.dispatchEvent(new CustomEvent('dropby:data-changed'))
  }
}

let started = false
let soonTimer = 0

function soon(): void {
  if (soonTimer) window.clearTimeout(soonTimer)
  soonTimer = window.setTimeout(() => {
    soonTimer = 0
    void sendKeptDrafts()
  }, 1500)
}

// Called once the app knows who is signed in (main.ts boot).
export function startAutoSend(): void {
  if (started) return
  started = true
  window.addEventListener('online', soon)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') soon()
  })
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void sendKeptDrafts()
  }, 60_000)
  soon()
}
